import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  Filter,
  Wrench,
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Printer,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Smartphone,
  TrendingUp,
  DollarSign,
  User,
  Phone,
  Tag,
  Download,
  Receipt,
  UserCheck,
  HelpCircle,
  RotateCcw,
  CornerUpLeft,
  Building2,
  Truck,
  X,
  Sparkles,
  MessageSquare,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { CompanyInfo, DeviceType, ServiceTicket, TicketStatus } from '../types';
import { calculateTicketTotal, formatDateTR, formatDateShortTR, formatTL, formatPhoneDisplay, getTicketContractBreakdown, STATUS_COLORS, STATUS_LABELS, getStatusColors } from '../utils/storage';
import { exportTicketIntakePDF, exportTicketInvoicePDF } from '../utils/pdfGenerator';

interface DashboardViewProps {
  tickets: ServiceTicket[];
  companyInfo: CompanyInfo;
  onSelectTicket: (ticket: ServiceTicket) => void;
  onOpenNewTicket: () => void;
  onPrintIntake: (ticket: ServiceTicket) => void;
  onPrintInvoice: (ticket: ServiceTicket) => void;
  onQuickUpdateStatus: (ticketId: string, newStatus: TicketStatus) => void;
  onOpenSms?: (ticket: ServiceTicket) => void;
  deviceTypes?: string[];
  currentUser?: import('../types').AppUser | null;
}

export type DashboardFilterTab =
  | 'aktif'
  | 'servise_alinan'
  | 'isleme_alinmayan'
  | 'islemde'
  | 'test_ediliyor'
  | 'sivida'
  | 'alt_servis'
  | 'parca'
  | 'onay'
  | 'onaylandi'
  | 'onaylanmadi'
  | 'hazir'
  | 'iade_edilecek'
  | 'iade_edildi'
  | 'tamamlanan'
  | 'tumu';

// Helper for Turkish case-insensitive & diacritic-agnostic comparison
const normalizeText = (text: string) => {
  return (text || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const resolveTicketDeviceArea = (
  ticket: ServiceTicket,
  assignments?: CompanyInfo['deviceAreaAssignments']
): 'printer' | 'computer' => {
  const type = ticket.deviceType || '';
  const lower = type.toLocaleLowerCase('tr-TR');
  const inferred: 'printer' | 'computer' =
    lower.includes('yazıcı') || lower.includes('yazici') ||
    lower.includes('tarayıcı') || lower.includes('tarayici') ||
    lower.includes('printer') || lower.includes('scanner') || lower.includes('fotokopi')
      ? 'printer'
      : 'computer';
  return assignments?.[type] || inferred;
};

export const DashboardView: React.FC<DashboardViewProps> = ({
  tickets = [],
  companyInfo,
  onSelectTicket,
  onOpenNewTicket,
  onPrintIntake,
  onPrintInvoice,
  onQuickUpdateStatus,
  onOpenSms,
  deviceTypes = [],
  currentUser,
}) => {
  const safeTickets = Array.isArray(tickets) ? tickets : [];
  const isLimitedTechnician = currentUser?.role === 'limited_technician';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScopeAll, setSearchScopeAll] = useState(true);
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('Tumu');
  const [selectedDeviceArea, setSelectedDeviceArea] = useState<'Tumu' | 'printer' | 'computer'>('Tumu');
  const [selectedWarranty, setSelectedWarranty] = useState<string>('Tumu');
  const [statusTab, setStatusTab] = useState<DashboardFilterTab>('aktif');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Reset page to 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusTab, selectedDeviceType, selectedDeviceArea, selectedWarranty, searchScopeAll]);

  // Dynamically collect all available device types for filtering
  const allFilterDeviceTypes = useMemo(() => {
    const set = new Set<string>(deviceTypes);
    for (let i = 0; i < safeTickets.length; i++) {
      const t = safeTickets[i];
      if (t?.deviceType) set.add(t.deviceType);
    }
    return Array.from(set).filter(Boolean);
  }, [deviceTypes, safeTickets]);

  // Counts and the results table share this exact device-area selection.
  const deviceAreaTickets = useMemo(() => {
    if (selectedDeviceArea === 'Tumu') return safeTickets;
    return safeTickets.filter(ticket =>
      ticket && resolveTicketDeviceArea(ticket, companyInfo.deviceAreaAssignments) === selectedDeviceArea
    );
  }, [safeTickets, selectedDeviceArea, companyInfo.deviceAreaAssignments]);
  const deviceAreaTicketSet = useMemo(() => new Set(deviceAreaTickets), [deviceAreaTickets]);

  // Compute Metrics in a single O(N) pass, memoized solely on safeTickets
  const {
    activeCount,
    serviceAcceptedCount,
    notYetServicedCount,
    repairCount,
    testingCount,
    liquidDamageCount,
    subServiceCount,
    partsWaitingCount,
    approvalWaitingCount,
    approvedCount,
    disapprovedCount,
    readyCount,
    returnWaitingCount,
    returnedCount,
    deliveredCount,
    thisMonthRevenue,
  } = useMemo(() => {
    let active = 0;
    let serviceAccepted = 0;
    let notYetServiced = 0;
    let repair = 0;
    let testing = 0;
    let liquidDamage = 0;
    let subService = 0;
    let partsWaiting = 0;
    let approvalWaiting = 0;
    let approved = 0;
    let disapproved = 0;
    let ready = 0;
    let returnWaiting = 0;
    let returned = 0;
    let delivered = 0;
    let monthRev = 0;

    const currentMonthKey = new Date().toISOString().substring(0, 7); // e.g. "2026-08"

    for (let i = 0; i < deviceAreaTickets.length; i++) {
      const t = deviceAreaTickets[i];
      if (!t) continue;
      const st = t.status;

      const isActive = st !== 'Hazir' && st !== 'TeslimEdildi' && st !== 'IadeEdildi' && st !== 'IadeEdilecek' && st !== 'Iptal';
      if (isActive) {
        active++;
        if (st === 'Kaydedildi') notYetServiced++;
        else serviceAccepted++;
      }
      if (st === 'Tamirde' || st === 'Incelemede') repair++;
      if (st === 'TestEdiliyor') testing++;
      if (st === 'Sivida') liquidDamage++;
      else if (st === 'AltServiste') subService++;
      else if (st === 'ParcaBekliyor') partsWaiting++;
      else if (st === 'MusteriOnayi') approvalWaiting++;
      else if (st === 'Onaylandi') approved++;
      else if (st === 'Onaylanmadi') disapproved++;
      else if (st === 'Hazir') ready++;
      else if (st === 'IadeEdilecek') returnWaiting++;
      else if (st === 'IadeEdildi') returned++;
      else if (st === 'TeslimEdildi') delivered++;

      if (t.createdAt && t.createdAt.startsWith(currentMonthKey)) {
        if (t.paymentStatus === 'Odendi' || st === 'TeslimEdildi') {
          const { grandTotal } = calculateTicketTotal(
            t.parts || [],
            t.laborCost || 0,
            t.vatRate || 0,
            t.discount || 0,
            t.isVatIncluded || false
          );
          monthRev += grandTotal;
        }
      }
    }

    return {
      activeCount: active,
      serviceAcceptedCount: serviceAccepted,
      notYetServicedCount: notYetServiced,
      repairCount: repair,
      testingCount: testing,
      liquidDamageCount: liquidDamage,
      subServiceCount: subService,
      partsWaitingCount: partsWaiting,
      approvalWaitingCount: approvalWaiting,
      approvedCount: approved,
      disapprovedCount: disapproved,
      readyCount: ready,
      returnWaitingCount: returnWaiting,
      returnedCount: returned,
      deliveredCount: delivered,
      thisMonthRevenue: monthRev,
    };
  }, [deviceAreaTickets]);

  const deviceStatusCounts = useMemo(() => {
    const counts = { printer: 0, computer: 0 };
    for (const ticket of safeTickets) {
      if (!ticket || ticket.status === 'Hazir' || ticket.status === 'TeslimEdildi' || ticket.status === 'IadeEdildi' || ticket.status === 'IadeEdilecek' || ticket.status === 'Iptal') continue;
      const area = resolveTicketDeviceArea(ticket, companyInfo.deviceAreaAssignments);
      counts[area]++;
    }
    return counts;
  }, [safeTickets, companyInfo.deviceAreaAssignments]);

  // Pre-index normalized strings for rapid search without repeating expensive normalizations on keypress
  const indexedTickets = useMemo(() => {
    return safeTickets
      .map(ticket => {
        if (!ticket) return null;
        const normName = normalizeText(ticket.customerName || '');
        const normPhoneDigits = (ticket.customerPhone || '').replace(/\D/g, '');
        const rawPhone = ticket.customerPhone || '';
        const normId = normalizeText(ticket.id || '');
        const normSerial = normalizeText(ticket.serialNumber || '');
        const normBrandModel = normalizeText(`${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`);
        const normSubService = normalizeText(ticket.subServiceName || '');

        return {
          ticket,
          normName,
          normPhoneDigits,
          rawPhone,
          normId,
          normSerial,
          normBrandModel,
          normSubService,
        };
      })
      .filter(Boolean) as Array<{
        ticket: ServiceTicket;
        normName: string;
        normPhoneDigits: string;
        rawPhone: string;
        normId: string;
        normSerial: string;
        normBrandModel: string;
        normSubService: string;
      }>;
  }, [safeTickets]);

  // Filtered Tickets List with High-Performance Index-Based Search
  const filteredTickets = useMemo(() => {
    const q = searchQuery.trim();
    const hasSearch = q.length > 0;
    const normalizedQ = hasSearch ? normalizeText(q) : '';
    const digitsOnlyQ = hasSearch ? q.replace(/\D/g, '') : '';

    const results: ServiceTicket[] = [];

    for (let i = 0; i < indexedTickets.length; i++) {
      const item = indexedTickets[i];
      const ticket = item.ticket;

      if (!deviceAreaTicketSet.has(ticket)) continue;

      // 1. Search Query Matching
      if (hasSearch) {
        const matchesName = item.normName.includes(normalizedQ);
        const matchesPhone = digitsOnlyQ ? item.normPhoneDigits.includes(digitsOnlyQ) : item.rawPhone.includes(q);
        const matchesId = item.normId.includes(normalizedQ);
        const matchesSerial = item.normSerial.includes(normalizedQ);
        const matchesModel = item.normBrandModel.includes(normalizedQ);
        const matchesSubService = item.normSubService.includes(normalizedQ);

        const isMatch = matchesName || matchesPhone || matchesId || matchesSerial || matchesModel || matchesSubService;
        if (!isMatch) continue;

        // If searchScopeAll is true, bypass tab filter to search across ALL records
        if (searchScopeAll) {
          if (selectedDeviceType !== 'Tumu' && ticket.deviceType !== selectedDeviceType) continue;
          if (selectedWarranty === 'evet' && !ticket.isUnderWarranty) continue;
          if (selectedWarranty === 'hayir' && ticket.isUnderWarranty) continue;
          results.push(ticket);
          continue;
        }
      }

      // 2. Tab Filter
      const st = ticket.status;
      if (statusTab === 'aktif') {
        if (st === 'Hazir' || st === 'TeslimEdildi' || st === 'IadeEdildi' || st === 'IadeEdilecek' || st === 'Iptal') continue;
      } else if (statusTab === 'servise_alinan') {
        if (st === 'Kaydedildi' || st === 'Hazir' || st === 'TeslimEdildi' || st === 'IadeEdildi' || st === 'IadeEdilecek' || st === 'Iptal') continue;
      } else if (statusTab === 'isleme_alinmayan') {
        if (st !== 'Kaydedildi') continue;
      } else if (statusTab === 'islemde') {
        if (st !== 'Tamirde' && st !== 'Incelemede') continue;
      } else if (statusTab === 'test_ediliyor') {
        if (st !== 'TestEdiliyor') continue;
      } else if (statusTab === 'sivida') {
        if (st !== 'Sivida') continue;
      } else if (statusTab === 'alt_servis') {
        if (st !== 'AltServiste') continue;
      } else if (statusTab === 'parca') {
        if (st !== 'ParcaBekliyor') continue;
      } else if (statusTab === 'onay') {
        if (st !== 'MusteriOnayi') continue;
      } else if (statusTab === 'onaylandi') {
        if (st !== 'Onaylandi') continue;
      } else if (statusTab === 'onaylanmadi') {
        if (st !== 'Onaylanmadi') continue;
      } else if (statusTab === 'hazir') {
        if (st !== 'Hazir') continue;
      } else if (statusTab === 'iade_edilecek') {
        if (st !== 'IadeEdilecek') continue;
      } else if (statusTab === 'iade_edildi') {
        if (st !== 'IadeEdildi') continue;
      } else if (statusTab === 'tamamlanan') {
        if (st !== 'TeslimEdildi') continue;
      }

      // 3. Device Type Filter
      if (selectedDeviceType !== 'Tumu' && ticket.deviceType !== selectedDeviceType) {
        continue;
      }

      // 4. Warranty Filter
      if (selectedWarranty === 'evet' && !ticket.isUnderWarranty) continue;
      if (selectedWarranty === 'hayir' && ticket.isUnderWarranty) continue;

      results.push(ticket);
    }

    // İlk alınan servis kaydı üstte, en yeni kayıt listenin altında görünsün.
    results.sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return (Number.isFinite(aDate) ? aDate : 0) - (Number.isFinite(bDate) ? bDate : 0);
    });
    return results;
  }, [indexedTickets, deviceAreaTicketSet, statusTab, selectedDeviceType, selectedWarranty, searchQuery, searchScopeAll]);

  // Paginated/Sliced Tickets for Fast DOM Rendering
  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / (pageSize > 0 ? pageSize : 1)));
  const displayedTickets = useMemo(() => {
    if (pageSize <= 0) return filteredTickets;
    const start = (currentPage - 1) * pageSize;
    return filteredTickets.slice(start, start + pageSize);
  }, [filteredTickets, currentPage, pageSize]);

  const applyDeviceAreaFilter = (area: 'printer' | 'computer') => {
    const alreadySelected = selectedDeviceArea === area;
    setSelectedDeviceArea(alreadySelected ? 'Tumu' : area);
    setSelectedDeviceType('Tumu');
    setSearchScopeAll(false);
  };

  const applyStatusFilter = (tab: DashboardFilterTab) => {
    setStatusTab(tab);
    setSelectedDeviceType('Tumu');
    setSearchScopeAll(false);
  };

  return (
    <div className="space-y-6">
      {/* High-Visibility Realtime Notification Banner for Pending Customer Approvals (Limited Technician only) */}
      {isLimitedTechnician && approvalWaitingCount > 0 && (
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white p-3.5 rounded-2xl shadow-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-amber-400/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center font-bold text-xl shrink-0 shadow-inner">
              🔔
            </div>
            <div>
              <div className="font-bold text-sm flex items-center gap-2">
                <span>DİKKAT: {approvalWaitingCount} Cihaz Müşteri Onayı Bekliyor!</span>
                <span className="bg-white text-orange-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-2xs animate-pulse">
                  Takip Gerekiyor
                </span>
              </div>
              <p className="text-xs text-amber-100 mt-0.5">
                Müşterilerle görüşme sağlayarak <b>[Onaylandı]</b> veya <b>[Onaylanmadı]</b> olarak güncelleyiniz.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStatusTab('onay')}
            className="w-full sm:w-auto px-4 py-2 bg-white hover:bg-amber-50 text-orange-900 font-bold text-xs rounded-xl shadow-md transition-all hover:scale-[1.02] cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
          >
            <span>Onay Bekleyenleri Listele ({approvalWaitingCount})</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Metrics Row - Clickable Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-[repeat(13,minmax(0,1fr))] gap-2">
        {/* 1. Active Tickets */}
        <button
          type="button"
          onClick={() => setStatusTab('aktif')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'aktif'
              ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-blue-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-slate-500">Aktif Cihazlar</p>
            <h3 className="text-lg font-bold text-slate-900 mt-0.5">{activeCount}</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Süreçteki toplam</p>
          </div>
        </button>

        <div className="grid grid-rows-2 gap-1.5 self-stretch">
          <button type="button" onClick={() => applyDeviceAreaFilter('printer')} className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${selectedDeviceArea === 'printer' ? 'border-blue-500 bg-blue-100 ring-1 ring-blue-300' : 'border-blue-100 bg-blue-50 hover:bg-blue-100'}`}>
            <span className="text-[9px] font-semibold leading-tight text-blue-700">Yazıcı</span>
            <span className="text-xs font-bold leading-tight text-blue-900">{deviceStatusCounts.printer}</span>
          </button>
          <button type="button" onClick={() => applyDeviceAreaFilter('computer')} className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${selectedDeviceArea === 'computer' ? 'border-indigo-500 bg-indigo-100 ring-1 ring-indigo-300' : 'border-indigo-100 bg-indigo-50 hover:bg-indigo-100'}`}>
            <span className="text-[9px] font-semibold leading-tight text-indigo-700">Bilgisayar</span>
            <span className="text-xs font-bold leading-tight text-indigo-900">{deviceStatusCounts.computer}</span>
          </button>
        </div>

        {/* 2. Repairing / Diagnostics */}
        <button
          type="button"
          onClick={() => setStatusTab('islemde')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'islemde'
              ? 'bg-purple-50/80 border-purple-500 ring-2 ring-purple-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-purple-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-slate-500">Arıza Tespiti / Onarım</p>
            <h3 className="text-lg font-bold text-purple-900 mt-0.5">{repairCount}</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Tezgahta / İncelemede</p>
          </div>
        </button>

        <div className="grid grid-rows-2 gap-1.5 self-stretch">
          <button type="button" onClick={() => applyStatusFilter('servise_alinan')} className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${statusTab === 'servise_alinan' ? 'border-emerald-500 bg-emerald-100 ring-1 ring-emerald-300' : 'border-emerald-100 bg-emerald-50 hover:bg-emerald-100'}`}>
            <span className="text-[9px] font-semibold leading-tight text-emerald-800">Servise Alınan</span>
            <span className="text-xs font-bold leading-tight text-emerald-900">{serviceAcceptedCount}</span>
          </button>
          <button type="button" onClick={() => applyStatusFilter('isleme_alinmayan')} className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${statusTab === 'isleme_alinmayan' ? 'border-amber-500 bg-amber-100 ring-1 ring-amber-300' : 'border-amber-100 bg-amber-50 hover:bg-amber-100'}`}>
            <span className="text-[9px] font-semibold leading-tight text-amber-800">Servise Alındı</span>
            <span className="text-xs font-bold leading-tight text-amber-900">{notYetServicedCount}</span>
          </button>
        </div>

        {/* Testing and liquid damage statuses, stacked beside the repair card */}
        <div className="grid grid-rows-2 gap-1.5 self-stretch">
          <button type="button" onClick={() => applyStatusFilter('test_ediliyor')} className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${statusTab === 'test_ediliyor' ? 'border-sky-500 bg-sky-100 ring-1 ring-sky-300' : 'border-sky-100 bg-sky-50 hover:bg-sky-100'}`}>
            <span className="text-[9px] font-semibold leading-tight text-sky-800">Test Ediliyor</span>
            <span className="text-xs font-bold leading-tight text-sky-900">{testingCount}</span>
          </button>
          <button type="button" onClick={() => applyStatusFilter('sivida')} className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${statusTab === 'sivida' ? 'border-blue-500 bg-blue-100 ring-1 ring-blue-300' : 'border-blue-100 bg-blue-50 hover:bg-blue-100'}`}>
            <span className="text-[9px] font-semibold leading-tight text-blue-800">Sıvıda</span>
            <span className="text-xs font-bold leading-tight text-blue-900">{liquidDamageCount}</span>
          </button>
        </div>

        {/* 3. Sub-Service / Alt Serviste */}
        <button
          type="button"
          onClick={() => setStatusTab('alt_servis')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'alt_servis'
              ? 'bg-cyan-50/90 border-cyan-500 ring-2 ring-cyan-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-cyan-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-cyan-700">Alt Serviste</p>
            <h3 className="text-lg font-bold text-cyan-800 mt-0.5">{subServiceCount}</h3>
            <p className="text-[10px] text-cyan-600 mt-0.5">Dış atölyede</p>
          </div>
        </button>

        {/* 4. Parts Waiting */}
        <button
          type="button"
          onClick={() => setStatusTab('parca')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'parca'
              ? 'bg-amber-50/90 border-amber-500 ring-2 ring-amber-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-amber-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-slate-500">Parça Bekleyen</p>
            <h3 className="text-lg font-bold text-amber-600 mt-0.5">{partsWaitingCount}</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Sipariş/kargoda</p>
          </div>
        </button>

        {/* 5. Customer Approval Pending */}
        <button
          type="button"
          onClick={() => setStatusTab('onay')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'onay'
              ? 'bg-orange-50/90 border-orange-500 ring-2 ring-orange-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-orange-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-orange-700">Onay Bekleyen</p>
            <h3 className="text-lg font-bold text-orange-800 mt-0.5">{approvalWaitingCount}</h3>
            <p className="text-[10px] text-orange-600 mt-0.5">Fiyat onayı</p>
          </div>
        </button>

        {/* 6. Approved (Onaylandı) */}
        <button
          type="button"
          onClick={() => setStatusTab('onaylandi')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'onaylandi'
              ? 'bg-teal-50/90 border-teal-500 ring-2 ring-teal-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-teal-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-teal-700">Onaylanan</p>
            <h3 className="text-lg font-bold text-teal-800 mt-0.5">{approvedCount}</h3>
            <p className="text-[10px] text-teal-600 mt-0.5">Müşteri onaylı</p>
          </div>
        </button>

        {/* 7. Disapproved (Onaylanmadı) */}
        <button
          type="button"
          onClick={() => setStatusTab('onaylanmadi')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'onaylanmadi'
              ? 'bg-rose-50/90 border-rose-500 ring-2 ring-rose-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-rose-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-rose-700">Onaylanmayan</p>
            <h3 className="text-lg font-bold text-rose-800 mt-0.5">{disapprovedCount}</h3>
            <p className="text-[10px] text-rose-600 mt-0.5">Onay verilmedi</p>
          </div>
        </button>

        {/* 8. Ready For Delivery */}
        <button
          type="button"
          onClick={() => setStatusTab('hazir')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'hazir'
              ? 'bg-emerald-50/90 border-emerald-500 ring-2 ring-emerald-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-emerald-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-slate-500">Teslimata Hazır</p>
            <h3 className="text-lg font-bold text-emerald-600 mt-0.5">{readyCount}</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Teslim bekliyor</p>
          </div>
        </button>

        {/* 9. İade Edilecek */}
        <button
          type="button"
          onClick={() => setStatusTab('iade_edilecek')}
          className={`text-left rounded-2xl p-3 border transition-all cursor-pointer relative ${
            statusTab === 'iade_edilecek'
              ? 'bg-rose-50/90 border-rose-500 ring-2 ring-rose-500/30 shadow-md scale-[1.02]'
              : 'bg-white border-slate-200/80 hover:border-rose-300 hover:shadow-md'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold text-rose-700">İade Edilecek</p>
            <h3 className="text-lg font-bold text-rose-800 mt-0.5">{returnWaitingCount}</h3>
            <p className="text-[10px] text-rose-500 mt-0.5">İade bekliyor</p>
          </div>
        </button>

        {/* Delivered and returned totals in a compact vertical stack */}
        <div className="grid grid-rows-2 gap-1.5 self-stretch">
          <button
            type="button"
            onClick={() => applyStatusFilter('tamamlanan')}
            className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${
              statusTab === 'tamamlanan'
                ? 'border-emerald-500 bg-emerald-100 ring-1 ring-emerald-300'
                : 'border-emerald-100 bg-emerald-50 hover:bg-emerald-100'
            }`}
          >
            <span className="text-[9px] font-semibold leading-tight text-emerald-800">Teslim Edildi</span>
            <span className="text-xs font-bold leading-tight text-emerald-900">{deliveredCount}</span>
          </button>
          <button
            type="button"
            onClick={() => applyStatusFilter('iade_edildi')}
            className={`flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 text-left transition-colors ${
              statusTab === 'iade_edildi'
                ? 'border-red-500 bg-red-100 ring-1 ring-red-300'
                : 'border-red-100 bg-red-50 hover:bg-red-100'
            }`}
          >
            <span className="text-[9px] font-semibold leading-tight text-red-800">İade Edildi</span>
            <span className="text-xs font-bold leading-tight text-red-900">{returnedCount}</span>
          </button>
        </div>
      </div>

      {/* Control Bar: Search & Status Filters */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          {/* Enhanced Real-Time Search Box */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-72 md:w-80">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Müşteri adı veya telefon ara..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8.5 pr-8 py-1.5 text-xs font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/60 shadow-2xs transition-all placeholder:text-slate-400"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  title="Aramayı Temizle"
                  className="absolute right-2.5 top-2 p-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Search Scope Switcher & Results Badge */}
            {searchQuery.trim() && (
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setSearchScopeAll(prev => !prev)}
                  className={`px-2 py-1.5 text-[11px] font-bold rounded-lg border transition-all flex items-center gap-1 cursor-pointer ${
                    searchScopeAll
                      ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-2xs'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                  }`}
                  title={searchScopeAll ? 'Şu anda durumdan bağımsız TÜM servis kayıtlarında aranıyor' : 'Sadece seçili sekmede aranıyor'}
                >
                  <Sparkles className="w-3 h-3 text-blue-600" />
                  <span>{searchScopeAll ? 'Tüm Kayıtlar' : 'Sekme İçi'}</span>
                </button>
                <span className="text-[11px] font-bold px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg whitespace-nowrap">
                  {filteredTickets.length} sonuç
                </span>
              </div>
            )}
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Device Type Select */}
            <div className="flex items-center space-x-1.5 bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200">
              <Smartphone className="w-3.5 h-3.5 text-slate-500" />
              <select
                value={selectedDeviceType}
                onChange={e => setSelectedDeviceType(e.target.value)}
                className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value="Tumu">Tüm Cihaz Türleri</option>
                {allFilterDeviceTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Warranty Filter */}
            <div className="flex items-center space-x-1.5 bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200">
              <ShieldCheck className="w-3.5 h-3.5 text-slate-500" />
              <select
                value={selectedWarranty}
                onChange={e => setSelectedWarranty(e.target.value)}
                className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value="Tumu">Tüm Cihazlar</option>
                <option value="evet">Garantili</option>
                <option value="hayir">Garantisiz</option>
              </select>
            </div>
          </div>
        </div>

        {/* Status Tabs Bar */}
        <div className="flex items-center space-x-1 border-t border-slate-100 pt-3 overflow-x-auto">
          <button
            onClick={() => setStatusTab('aktif')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'aktif'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Devam Eden Servisler ({activeCount})
          </button>

          <button onClick={() => applyStatusFilter('servise_alinan')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${statusTab === 'servise_alinan' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
            Servise Alınan ({serviceAcceptedCount})
          </button>
          <button onClick={() => applyStatusFilter('isleme_alinmayan')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${statusTab === 'isleme_alinmayan' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
            Servise Alındı ({notYetServicedCount})
          </button>

          <button
            onClick={() => setStatusTab('islemde')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'islemde'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            İşlemde / Teşhiste ({repairCount})
          </button>

          <button
            onClick={() => setStatusTab('test_ediliyor')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${statusTab === 'test_ediliyor' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Test Ediliyor ({testingCount})
          </button>

          <button
            onClick={() => setStatusTab('sivida')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${statusTab === 'sivida' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Sıvıda ({liquidDamageCount})
          </button>

          <button
            onClick={() => setStatusTab('alt_servis')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'alt_servis'
                ? 'bg-cyan-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Alt Serviste ({subServiceCount})
          </button>

          <button
            onClick={() => setStatusTab('parca')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'parca'
                ? 'bg-amber-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Parça Bekleyen ({partsWaitingCount})
          </button>

          <button
            onClick={() => setStatusTab('onay')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'onay'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Onay Bekleyenler ({approvalWaitingCount})
          </button>

          <button
            onClick={() => setStatusTab('onaylandi')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'onaylandi'
                ? 'bg-teal-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Onaylananlar ({approvedCount})
          </button>

          <button
            onClick={() => setStatusTab('onaylanmadi')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'onaylanmadi'
                ? 'bg-rose-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Onaylanmayanlar ({disapprovedCount})
          </button>

          <button
            onClick={() => setStatusTab('hazir')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'hazir'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Teslimata Hazır ({readyCount})
          </button>

          <button
            onClick={() => setStatusTab('iade_edilecek')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'iade_edilecek'
                ? 'bg-rose-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            İade Edilecek ({returnWaitingCount})
          </button>

          <button
            onClick={() => setStatusTab('iade_edildi')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'iade_edildi'
                ? 'bg-red-700 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            İade Edildi ({returnedCount})
          </button>

          <button
            onClick={() => setStatusTab('tamamlanan')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'tamamlanan'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Teslim Edilenler ({deliveredCount})
          </button>

          <button
            onClick={() => setStatusTab('tumu')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
              statusTab === 'tumu'
                ? 'bg-slate-700 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Tüm Kayıtlar ({tickets.length})
          </button>
        </div>
      </div>

      {/* Service Tickets Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {/* Table Header Active Filter Label */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="font-bold text-slate-700 flex items-center space-x-1.5">
            <Filter className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span>
              {searchQuery.trim()
                ? `"${searchQuery}" araması için bulunan kayıtlar (${filteredTickets.length}) ${searchScopeAll ? '— [Tüm Kayıtlar]' : ''}`
                : statusTab === 'aktif'
                ? `Aktif Devam Eden Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'servise_alinan'
                ? `Servise Alınan Kayıtlar (${filteredTickets.length})`
                : statusTab === 'isleme_alinmayan'
                ? `Servise Alındı Kayıtları (${filteredTickets.length})`
                : statusTab === 'islemde'
                ? `İşlemde / Teşhiste Olan Kayıtlar (${filteredTickets.length})`
                : statusTab === 'alt_servis'
                ? `Alt Serviste Olan Kayıtlar (${filteredTickets.length})`
                : statusTab === 'parca'
                ? `Parça Bekleyen Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'onay'
                ? `Müşteri Onayı Bekleyen Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'onaylandi'
                ? `Müşteri Onaylı Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'onaylanmadi'
                ? `Müşteri Onaylamayan (Reddedilen) Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'hazir'
                ? `Teslimata Hazır Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'iade_edilecek'
                ? `İade Edilecek Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'iade_edildi'
                ? `İade Edilmiş Servis Kayıtları (${filteredTickets.length})`
                : statusTab === 'tamamlanan'
                ? `Teslim Edilen Servis Kayıtları (${filteredTickets.length})`
                : `Tüm Servis Kayıtları Listesi (${filteredTickets.length})`}
            </span>
          </span>
          <div className="flex items-center gap-3">
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-rose-600 hover:text-rose-800 font-semibold text-[11px] hover:underline cursor-pointer"
              >
                Aramayı Sıfırla
              </button>
            )}
            {statusTab !== 'tumu' && !searchQuery && (
              <button
                type="button"
                onClick={() => setStatusTab('tumu')}
                className="text-blue-600 hover:text-blue-800 font-semibold text-[11px] hover:underline cursor-pointer"
              >
                Filtreyi Temizle (Tümünü Göster)
              </button>
            )}
          </div>
        </div>

        {/* Mobile View: High-density interactive Cards for Smartphones (md:hidden) */}
        <div className="block md:hidden divide-y divide-slate-100">
          {displayedTickets.length > 0 ? (
            displayedTickets.map(t => {
              const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
              const statusInfo = getStatusColors(t.status);
              const isContract = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId);
              const contractBreakdown = getTicketContractBreakdown(t);

              return (
                <div
                  key={t.id}
                  onClick={() => onSelectTicket(t)}
                  className="p-3.5 bg-white hover:bg-slate-50 active:bg-slate-100 transition-colors space-y-2.5 cursor-pointer"
                >
                  {/* Top Bar: Code + Date + Status Badge */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-extrabold text-blue-700 text-xs px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md">
                        {t.id}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {formatDateShortTR(t.createdAt)}
                      </span>
                    </div>

                    <div className="shrink-0" onClick={e => e.stopPropagation()}>
                      <select
                        value={t.status}
                        onChange={e => {
                          const newSt = e.target.value as TicketStatus;
                          if (newSt === t.status) return;
                          if (newSt === 'TeslimEdildi' || newSt === 'IadeEdildi') {
                            const confirmMsg = `Dikkat: Bu cihazı "${STATUS_LABELS[newSt]}" durumuna almak istediğinize emin misiniz?\n(Cihaz aktif listeden arşive aktarılacaktır)`;
                            if (!window.confirm(confirmMsg)) {
                              return;
                            }
                          }
                          onQuickUpdateStatus(t.id, newSt);
                        }}
                        className={`text-[11px] font-bold px-2 py-1 rounded-lg border focus:outline-none cursor-pointer ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                      >
                        {!isLimitedTechnician ? (
                          <>
                            <option value="Kaydedildi">Kayıt Alındı</option>
                            <option value="Incelemede">Arıza Tespiti</option>
                            <option value="TestEdiliyor">Test Ediliyor</option>
                            <option value="Sivida">Sıvıda</option>
                            <option value="MusteriOnayi">Onay Bekliyor</option>
                            <option value="Tamirde">Onarımda</option>
                            <option value="ParcaBekliyor">Parça Bekliyor</option>
                            <option value="AltServiste">Alt Serviste</option>
                            <option value="Onaylandi">✓ Onaylandı</option>
                            <option value="Onaylanmadi">✕ Onaylanmadı</option>
                            <option value="Hazir">Teslimata Hazır</option>
                            <option value="IadeEdilecek">İade Edilecek</option>
                            <option value="IadeEdildi">İade Edildi</option>
                            <option value="TeslimEdildi">Teslim Edildi</option>
                            <option value="Iptal">İptal Edildi</option>
                          </>
                        ) : (
                          <>
                            {t.status !== 'TeslimEdildi' && t.status !== 'IadeEdildi' && t.status !== 'Onaylandi' && t.status !== 'Onaylanmadi' && (
                              <option value={t.status} disabled>{STATUS_LABELS[t.status]} (Mevcut)</option>
                            )}
                            <option value="Onaylandi">✓ Onaylandı (Onay Verildi)</option>
                            <option value="Onaylanmadi">✕ Onaylanmadı (Reddedildi)</option>
                            <option value="IadeEdildi">İade Edildi</option>
                            <option value="TeslimEdildi">Teslim Edildi</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Customer Info & Direct Call Button */}
                  <div className="flex items-center justify-between gap-2 pt-0.5">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-800 text-sm truncate flex items-center gap-1.5">
                        <span>{t.customerName}</span>
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 font-medium">
                        <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                        <span>{formatPhoneDisplay(t.customerPhone)}</span>
                      </div>
                    </div>

                    <a
                      href={`tel:${t.customerPhone.replace(/[^0-9+]/g, '')}`}
                      onClick={e => e.stopPropagation()}
                      title="Müşteriyi Ara"
                      className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-bold flex items-center gap-1 shadow-2xs shrink-0"
                    >
                      <Phone className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Ara</span>
                    </a>
                  </div>

                  {/* Device Info & Warranty */}
                  <div className="bg-slate-50/80 rounded-xl p-2 border border-slate-200/70 text-xs space-y-1">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-semibold text-slate-800 truncate">
                        {t.deviceBrand} {t.deviceModel}
                      </span>
                      <span className="text-[10px] text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200 shrink-0 font-medium">
                        {t.deviceType}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-1 text-[10px]">
                      {t.isUnderWarranty ? (
                        <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded font-bold flex items-center gap-0.5">
                          <ShieldCheck className="w-2.5 h-2.5" /> Garanti Var
                        </span>
                      ) : (
                        <span className="bg-slate-200/70 text-slate-600 px-1.5 py-0.2 rounded font-medium">
                          Garanti Dışı
                        </span>
                      )}

                      {(t.subServiceName || t.status === 'AltServiste') && (
                        <span className="bg-cyan-50 text-cyan-800 border border-cyan-200 px-1.5 py-0.2 rounded font-medium flex items-center gap-0.5">
                          <Building2 className="w-2.5 h-2.5 text-cyan-600" />
                          {t.subServiceName || 'Alt Servis'}
                        </span>
                      )}

                      {/* Technician */}
                      <span className="text-slate-500 flex items-center gap-0.5">
                        👨‍🔧 {t.assignedTechnicianName || t.assignedTechnicianUsername || 'Boşta'}
                      </span>
                    </div>

                    {/* Complaint/Issue Snippet */}
                    <p className="text-slate-600 text-[11px] line-clamp-2 pt-0.5 italic">
                      "{t.issueDescription}"
                    </p>
                  </div>

                  {/* 1-Click Quick Approval Buttons on Mobile */}
                  {t.status === 'MusteriOnayi' && (
                    <div
                      className="bg-orange-50/80 border border-orange-200 rounded-xl p-2 flex items-center justify-between gap-2"
                      onClick={e => e.stopPropagation()}
                    >
                      <span className="text-[11px] font-bold text-orange-900">
                        Müşteri Kararı:
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => onQuickUpdateStatus(t.id, 'Onaylandi')}
                          className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-xs flex items-center gap-1 cursor-pointer"
                        >
                          ✓ Onaylandı
                        </button>
                        <button
                          type="button"
                          onClick={() => onQuickUpdateStatus(t.id, 'Onaylanmadi')}
                          className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold shadow-xs flex items-center gap-1 cursor-pointer"
                        >
                          ✕ Reddedildi
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Price & Action Toolbar */}
                  <div
                    className="flex items-center justify-between pt-1 border-t border-slate-100"
                    onClick={e => e.stopPropagation()}
                  >
                    <div>
                      <span className="text-[10px] text-slate-400 block leading-tight">Toplam Tutar:</span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-bold text-slate-900 text-sm">
                          {formatTL(grandTotal)}
                        </span>
                        {isContract ? (
                          <span className="text-[9px] px-1.5 py-0.2 rounded font-bold bg-purple-100 text-purple-800">
                            Sözleşmeli
                          </span>
                        ) : (t.isUnderWarranty || t.serviceType === 'Garantili' || t.serviceType === 'ServisGarantili') && grandTotal <= 0 ? (
                          <span className="text-[9px] px-1.5 py-0.2 rounded font-bold bg-emerald-100 text-emerald-800">Garanti kapsamında · Ücret yok</span>
                        ) : t.paymentStatus === 'KismiOdendi' ? (
                          <span className="text-[9px] px-1.5 py-0.2 rounded font-bold bg-amber-100 text-amber-800">
                            Kısmi ({formatTL(t.paidAmount || 0)})
                          </span>
                        ) : (
                          <span
                            className={`text-[9px] px-1.5 py-0.2 rounded font-bold ${
                              t.paymentStatus === 'Odendi'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {t.paymentStatus === 'Odendi' ? 'Ödendi' : 'Ödenmedi'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      {onOpenSms && (
                        <button
                          type="button"
                          onClick={() => onOpenSms(t)}
                          title="SMS Gönder"
                          className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 active:scale-95 rounded-lg transition-all cursor-pointer"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => onPrintIntake(t)}
                        title="Form Yazdır"
                        className="p-2 text-slate-700 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded-lg transition-all cursor-pointer"
                      >
                        <Printer className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => onPrintInvoice(t)}
                        title="Makbuz / Fatura"
                        className="p-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 active:scale-95 rounded-lg transition-all cursor-pointer"
                      >
                        <Receipt className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => onSelectTicket(t)}
                        className="p-2 text-white bg-slate-900 hover:bg-slate-800 active:scale-95 rounded-lg text-xs font-bold flex items-center gap-0.5 transition-all shadow-xs cursor-pointer"
                      >
                        <span>Detay</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-10 px-4 text-center text-slate-400 space-y-3">
              <Search className="w-8 h-8 mx-auto text-slate-300" />
              <p className="font-semibold text-slate-700 text-sm">
                {searchQuery.trim()
                  ? `"${searchQuery}" aramasıyla eşleşen servis kaydı bulunamadı.`
                  : 'Aranan kriterlere uygun servis kaydı bulunamadı.'}
              </p>
              <button
                type="button"
                onClick={onOpenNewTicket}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
              >
                + Yeni Cihaz Kaydı
              </button>
            </div>
          )}
        </div>

        {/* Desktop View: Full Data Table (hidden md:block) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                <th className="py-3 px-4">Servis Kodu / Tarih</th>
                <th className="py-3 px-4">Müşteri Bilgisi</th>
                <th className="py-3 px-4">Cihaz & Garanti</th>
                <th className="py-3 px-4">Arıza / Şikayet</th>
                <th className="py-3 px-4">Aşama & Durum</th>
                <th className="py-3 px-4 text-right">Tutar & Ödeme</th>
                <th className="py-3 px-4 text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {displayedTickets.length > 0 ? (
                displayedTickets.map(t => {
                  const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                  const statusInfo = getStatusColors(t.status);

                  return (
                    <tr
                      key={t.id}
                      className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                      onClick={() => onSelectTicket(t)}
                    >
                      {/* Ticket ID & Date */}
                      <td className="py-3 px-4">
                        <span className="font-mono font-bold text-blue-700 block text-xs">
                          {t.id}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {formatDateShortTR(t.createdAt)}
                        </span>
                      </td>

                      {/* Customer Info */}
                      <td className="py-3 px-4">
                        <span className="font-bold text-slate-800 block">{t.customerName}</span>
                        <span className="text-[11px] text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3 text-slate-400" />
                          {formatPhoneDisplay(t.customerPhone)}
                        </span>
                      </td>

                      {/* Device & Warranty */}
                      <td className="py-3 px-4">
                        <span className="font-semibold text-slate-800 block">
                          {t.deviceBrand} {t.deviceModel}
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          <span className="text-[11px] text-slate-500">{t.deviceType}</span>
                          {t.isUnderWarranty ? (
                            <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] px-1.5 py-0.2 rounded font-bold flex items-center gap-0.5">
                              <ShieldCheck className="w-2.5 h-2.5" /> Garanti Var
                            </span>
                          ) : (
                            <span className="bg-slate-100 text-slate-500 text-[10px] px-1.5 py-0.2 rounded font-medium">
                              Garanti Dışı
                            </span>
                          )}
                          {(t.subServiceName || (t.subServiceCost && t.subServiceCost > 0) || t.status === 'AltServiste') && (
                            <span className="bg-cyan-50 text-cyan-800 border border-cyan-200 text-[10px] px-1.5 py-0.2 rounded font-medium flex items-center gap-0.5">
                              <Building2 className="w-2.5 h-2.5 text-cyan-600" />
                              {t.subServiceName || 'Alt Servis'}
                              {t.subServiceCost ? ` (${formatTL(t.subServiceCost)})` : ''}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Issue Description */}
                      <td className="py-3 px-4 max-w-xs">
                        <p className="text-slate-600 line-clamp-2 leading-relaxed text-[11px]">
                          {t.issueDescription}
                        </p>
                      </td>

                      {/* Status Dropdown & Quick Actions */}
                      <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-col gap-1.5">
                          <select
                            value={t.status}
                            onChange={e => {
                              const newSt = e.target.value as TicketStatus;
                              if (newSt === t.status) return;
                              if (newSt === 'TeslimEdildi' || newSt === 'IadeEdildi') {
                                const confirmMsg = `Dikkat: Bu cihazı "${STATUS_LABELS[newSt]}" durumuna almak istediğinize emin misiniz?\n(Cihaz aktif listeden arşive aktarılacaktır)`;
                                if (!window.confirm(confirmMsg)) {
                                  return;
                                }
                              }
                              onQuickUpdateStatus(t.id, newSt);
                            }}
                            className={`text-xs px-2.5 py-1 rounded-lg font-bold border focus:outline-none cursor-pointer ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                          >
                            {!isLimitedTechnician ? (
                              <>
                                <option value="Kaydedildi">Kayıt Alındı</option>
                                <option value="Incelemede">Arıza Tespiti</option>
                                <option value="TestEdiliyor">Test Ediliyor</option>
                                <option value="Sivida">Sıvıda</option>
                                <option value="MusteriOnayi">Onay Bekliyor</option>
                                <option value="Tamirde">Onarımda</option>
                                <option value="ParcaBekliyor">Parça Bekliyor</option>
                                <option value="AltServiste">Alt Serviste</option>
                                <option value="Onaylandi">✓ Onaylandı</option>
                                <option value="Onaylanmadi">✕ Onaylanmadı</option>
                                <option value="Hazir">Teslimata Hazır</option>
                                <option value="IadeEdilecek">İade Edilecek</option>
                                <option value="IadeEdildi">İade Edildi</option>
                                <option value="TeslimEdildi">Teslim Edildi</option>
                                <option value="Iptal">İptal Edildi</option>
                              </>
                            ) : (
                              <>
                                {t.status !== 'TeslimEdildi' && t.status !== 'IadeEdildi' && t.status !== 'Onaylandi' && t.status !== 'Onaylanmadi' && (
                                  <option value={t.status} disabled>{STATUS_LABELS[t.status]} (Mevcut)</option>
                                )}
                                <option value="Onaylandi">✓ Onaylandı (Onay Verildi)</option>
                                <option value="Onaylanmadi">✕ Onaylanmadı (Reddedildi)</option>
                                <option value="IadeEdildi">İade Edildi</option>
                                <option value="TeslimEdildi">Teslim Edildi</option>
                              </>
                            )}
                          </select>

                          {/* Quick 1-click Approval Buttons when in MusteriOnayi status */}
                          {t.status === 'MusteriOnayi' && (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => onQuickUpdateStatus(t.id, 'Onaylandi')}
                                title="Müşteri Onayladı"
                                className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold shadow-2xs flex items-center gap-0.5 transition-transform hover:scale-105 cursor-pointer"
                              >
                                ✓ Onaylı
                              </button>
                              <button
                                type="button"
                                onClick={() => onQuickUpdateStatus(t.id, 'Onaylanmadi')}
                                title="Müşteri Onay Vermedi"
                                className="px-2 py-0.5 bg-rose-600 hover:bg-rose-700 text-white rounded text-[10px] font-bold shadow-2xs flex items-center gap-0.5 transition-transform hover:scale-105 cursor-pointer"
                              >
                                ✕ Onaylanmadı
                              </button>
                            </div>
                          )}

                          {/* Technician badge */}
                          <div className="flex items-center gap-1 text-[10px] text-slate-500 pt-0.5">
                            <span>👨‍🔧</span>
                            <span className={`truncate max-w-[120px] ${
                              (currentUser && (t.assignedTechnicianId === currentUser.id || t.assignedTechnicianUsername === currentUser.username))
                                ? 'text-indigo-700 font-bold bg-indigo-50 px-1 py-0.2 rounded border border-indigo-200'
                                : t.assignedTechnicianName
                                ? 'text-slate-700 font-medium'
                                : 'text-slate-400 italic'
                            }`}>
                              {(currentUser && (t.assignedTechnicianId === currentUser.id || t.assignedTechnicianUsername === currentUser.username))
                                ? 'Size Atandı'
                                : t.assignedTechnicianName || t.assignedTechnicianUsername || t.repairedByName || 'Boşta'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Pricing & Payment */}
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono font-bold text-slate-900 block text-sm">
                          {formatTL(grandTotal)}
                        </span>
                        {(() => {
                          const isContract = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId);
                          const contractBreakdown = getTicketContractBreakdown(t);

                          if (isContract) {
                            if (!contractBreakdown.hasOutOfContractParts) {
                              return (
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold inline-block bg-purple-100 text-purple-800 border border-purple-200">
                                  Sözleşme Kapsamında (Ödendi)
                                </span>
                              );
                            }
                            if (contractBreakdown.effectiveDebt <= 0.01) {
                              return (
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold inline-block bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  Harici Ödendi
                                </span>
                              );
                            }
                            return (
                              <div className="text-[10px] space-y-0.5">
                                <span className="bg-amber-100 text-amber-900 px-1.5 py-0.2 rounded font-bold inline-block border border-amber-200">
                                  Harici Borç
                                </span>
                                <span className="text-rose-600 font-bold block font-mono">
                                  {formatTL(contractBreakdown.effectiveDebt)}
                                </span>
                              </div>
                            );
                          }

                          if ((t.isUnderWarranty || t.serviceType === 'Garantili' || t.serviceType === 'ServisGarantili') && grandTotal <= 0) {
                            return (
                              <span className="text-[10px] px-1.5 py-0.2 rounded font-bold inline-block bg-emerald-100 text-emerald-800 border border-emerald-200">
                                Garanti kapsamında · Ücret yok
                              </span>
                            );
                          }

                          if (t.paymentStatus === 'KismiOdendi') {
                            return (
                              <div className="text-[10px] space-y-0.5">
                                <span className="bg-amber-100 text-amber-900 px-1.5 py-0.2 rounded font-bold inline-block">
                                  Alınan: {formatTL(t.paidAmount || 0)}
                                </span>
                                <span className="text-rose-600 font-bold block font-mono">
                                  Kalan: {formatTL(Math.max(0, grandTotal - (t.paidAmount || 0)))}
                                </span>
                              </div>
                            );
                          }

                          return (
                            <span
                              className={`text-[10px] px-1.5 py-0.2 rounded font-bold inline-block ${
                                t.paymentStatus === 'Odendi'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-rose-100 text-rose-800'
                              }`}
                            >
                              {t.paymentStatus === 'Odendi' ? 'Ödendi' : 'Ödenmedi'}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Quick Actions */}
                      <td className="py-3 px-4 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center space-x-1">
                          {onOpenSms && (
                            <button
                              onClick={() => onOpenSms(t)}
                              title="Müşteriye SMS Gönder"
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                            </button>
                          )}

                          <button
                            onClick={() => onPrintIntake(t)}
                            title="Müşteri Kabul Formu Yazdır"
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Printer className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => onPrintInvoice(t)}
                            title="Servis Fatura / Makbuz Aç ve Yazdır"
                            className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                          >
                            <Receipt className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => onSelectTicket(t)}
                            title="Detay & Düzenle"
                            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <div className="max-w-sm mx-auto space-y-3">
                      <Search className="w-8 h-8 mx-auto text-slate-300" />
                      <p className="font-semibold text-slate-700">
                        {searchQuery.trim()
                          ? `"${searchQuery}" aramasıyla eşleşen servis kaydı bulunamadı.`
                          : 'Aranan kriterlere uygun servis kaydı bulunamadı.'}
                      </p>
                      <p className="text-xs text-slate-400">
                        Müşteri adını veya telefon numarasını kontrol edebilir ya da yeni bir kayıt açabilirsiniz.
                      </p>
                      <div className="flex items-center justify-center gap-2 pt-1">
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-all cursor-pointer"
                          >
                            Aramayı Temizle
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={onOpenNewTicket}
                          className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                        >
                          + Yeni Cihaz Kaydı
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Fast Browsing Bar */}
        {filteredTickets.length > 0 && (
          <div className="px-4 py-3 bg-slate-50/90 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
            {/* Range and count info */}
            <div className="flex items-center gap-2 font-medium">
              <span>
                Toplam <b className="text-slate-800">{filteredTickets.length}</b> kayıttan{' '}
                {pageSize > 0 ? (
                  <>
                    <b className="text-slate-800">{(currentPage - 1) * pageSize + 1}</b> -{' '}
                    <b className="text-slate-800">
                      {Math.min(currentPage * pageSize, filteredTickets.length)}
                    </b>{' '}
                    arası gösteriliyor
                  </>
                ) : (
                  <b>tümü gösteriliyor</b>
                )}
              </span>
            </div>

            {/* Page Size Selector & Navigation Buttons */}
            <div className="flex items-center gap-3">
              {/* Page size dropdown */}
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-slate-500 font-medium">Sayfa Başına:</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer shadow-2xs"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={-1}>Tümü</option>
                </select>
              </div>

              {/* Page controls (only shown when pageSize > 0 and totalPages > 1) */}
              {pageSize > 0 && totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="İlk Sayfa"
                    className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 transition-colors cursor-pointer shadow-2xs"
                  >
                    <ChevronsLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    title="Önceki Sayfa"
                    className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 transition-colors cursor-pointer shadow-2xs"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  <span className="px-2.5 py-1 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg shadow-2xs">
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    title="Sonraki Sayfa"
                    className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 transition-colors cursor-pointer shadow-2xs"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    title="Son Sayfa"
                    className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 transition-colors cursor-pointer shadow-2xs"
                  >
                    <ChevronsRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
